import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import logger from 'src/utils/logger';
import {useDropzone} from 'react-dropzone';
import {Button} from '@spaced-out/ui-design-system';
import {type Part} from 'src/components/agentic-ui';
import css from './editor.css';

/* ---------- Lexical ---------- */
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  TextNode,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  $createParagraphNode,
  $createTextNode,
} from 'lexical';
import {$convertToMarkdownString, TRANSFORMERS} from '@lexical/markdown';

/* ===================== Types ===================== */
export type MentionUser = {id: string; label: string; avatarUrl?: string};
export type UploadResult = {
  id: string;
  url: string;
  name: string;
  mime: string;
  size: number;
};

type MentionData = {id: string; label: string};
type AttachmentAttrs = {
  id: string;
  name: string;
  size: number;
  mime: string;
  url?: string;
  kbLabel: string;
};

export interface EditorProps {
  placeholder?: string;
  fetchMentionSuggestions?: (q: string) => Promise<MentionUser[]>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
  onSend: (parts: Part[]) => Promise<void> | void;
  className?: string;
  renderBottomBar?: (props: {
    onSend: () => Promise<void>;
    insertMentionTrigger: () => void;
  }) => React.ReactNode;
  disableSend?: boolean;
  autoFocus?: boolean;
}

export interface EditorHandle {
  setPlainText: (text: string) => void;
  focus: () => void;
  clear: () => void;
}

/* ================= Lexical custom nodes ================ */
// Mention
class MentionNode extends TextNode {
  __data: MentionData;
  static override getType() {
    return 'mention';
  }
  static override clone(n: MentionNode) {
    return new MentionNode(n.__text, n.__data, n.__key);
  }
  constructor(text: string, data: MentionData, key?: string) {
    super(text, key);
    this.__data = data;
  }
  override createDOM() {
    const span = document.createElement('span');
    span.className = css.mentionChip;
    span.textContent = `@${this.__data.label}`;
    return span;
  }
  override exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'mention',
      version: 1,
      data: this.__data,
    };
  }
  static override importJSON(s: any) {
    const n = new MentionNode(`@${s?.data?.label ?? ''}`, s.data);
    (n as any).setMode?.('token');
    return n;
  }
  override isInline(): true {
    return true;
  }
  override canInsertTextBefore() {
    return false;
  }
  override canInsertTextAfter() {
    return false;
  }
  override exportDOM(): DOMExportOutput {
    const el = document.createElement('span');
    el.setAttribute('data-mention', this.__data.id);
    el.textContent = `@${this.__data.label}`;
    return {element: el};
  }
  static override importDOM(): DOMConversionMap | null {
    return null;
  }
}
const $createMentionNode = (d: MentionData) => {
  const n = new MentionNode(`@${d.label}`, d);
  (n as any).setMode?.('token');
  return n;
};

// Attachment chip
class AttachmentNode extends DecoratorNode<JSX.Element> {
  __attrs: AttachmentAttrs;
  static override getType() {
    return 'attachment';
  }
  static override clone(n: AttachmentNode) {
    return new AttachmentNode(n.__attrs, n.__key);
  }
  constructor(attrs: AttachmentAttrs, key?: string) {
    super(key);
    this.__attrs = attrs;
  }
  override isInline() {
    return true;
  }
  override isIsolated() {
    return true;
  }
  override createDOM() {
    const span = document.createElement('span');
    return span;
  }
  override updateDOM() {
    return false;
  }
  override decorate() {
    const a = this.__attrs;
    const sizeKB = Math.max(1, Math.round(a.size / 1024));
    return (
      <span
        contentEditable={false}
        className={css.attachmentChip}
        title={a.mime}
      >
        <span>{a.name}</span>
        <span className={css.muted}>
          {sizeKB}
          {` ${a.kbLabel}`}
        </span>
      </span>
    );
  }
  override exportJSON() {
    return {type: 'attachment', version: 1, attrs: this.__attrs};
  }
  static override importJSON(s: any) {
    return new AttachmentNode(s.attrs);
  }
}
const $createAttachmentNode = (a: AttachmentAttrs) => new AttachmentNode(a);

/* ================= Editor Theme ================ */
const EditorTheme = () => ({
  paragraph: css.editorParagraph,
  text: {
    bold: css.textBold,
    italic: css.textItalic,
    underline: css.textUnderline,
    strikethrough: css.textStrikethrough,
  },
});

/* ================= Plugins ================ */
function MentionTypeahead({
  fetchMentionSuggestions,
}: {
  fetchMentionSuggestions?: (q: string) => Promise<MentionUser[]>;
}) {
  const [editor] = useLexicalComposerContext();
  class MentionOption extends MenuOption {
    id: string;
    label: string;
    constructor(user: MentionUser) {
      super(user.id);
      this.id = user.id;
      this.label = user.label;
    }
  }
  const [items, setItems] = useState<MentionOption[]>([]);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const onQueryChange = useCallback(
    async (q: string | null) => {
      const res = (await fetchMentionSuggestions?.(q ?? '')) ?? [];
      setItems(res.slice(0, 8).map((u) => new MentionOption(u)));
    },
    [fetchMentionSuggestions],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      triggerFn={(text) => {
        const m = /(?:^|\s)@([\w-]{0,32})$/.exec(text);
        if (!m) {
          return null;
        }
        return {
          leadOffset: m.index + m[0].lastIndexOf('@'),
          matchingString: m[1],
          replaceableString: m[0].slice(m[0].lastIndexOf('@')),
        };
      }}
      onQueryChange={onQueryChange}
      onSelectOption={(item, nodeToReplace) => {
        editor.update(() => {
          const n = $createMentionNode({id: item.id, label: item.label});
          nodeToReplace?.replace(n);
        });
      }}
      options={items}
      onOpen={(r) => setAnchorRect(r.getRect())}
      onClose={() => setAnchorRect(null)}
      menuRenderFn={() => {
        if (!anchorRect) {
          return null;
        }
        return (
          <div
            style={{
              position: 'absolute',
              top: anchorRect.bottom + window.scrollY,
              left: anchorRect.left + window.scrollX,
            }}
            className={css.typeaheadMenu}
          >
            {items.map((u) => (
              <div
                key={u.id}
                className={css.typeaheadOption}
                onMouseDown={(e) => {
                  e.preventDefault();
                  (document.activeElement as HTMLElement)?.blur?.();
                }}
              >
                {u.label}
              </div>
            ))}
          </div>
        );
      }}
    />
  );
}

function useAttachmentPasting(
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>,
) {
  const [editor] = useLexicalComposerContext();
  return useCallback(
    async (files: File[]) => {
      await editor.update(async () => {
        const temps: AttachmentAttrs[] = files.map((f) => ({
          id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
          name: f.name,
          size: f.size,
          mime: f.type || 'application/octet-stream',
          kbLabel: 'KB',
        }));
        const sel = $getSelection();
        temps.forEach((t, idx) => {
          sel?.insertNodes([$createAttachmentNode(t)]);
          if (idx < temps.length - 1) {
            sel?.insertText(' ');
          }
        });

        if (onUploadFiles) {
          const uploaded = await onUploadFiles(files);
          const root = $getRoot();
          const all = root.getChildren();
          all.forEach((n: any) => {
            if (n instanceof AttachmentNode) {
              n.remove();
            }
          });
          uploaded.forEach((u, idx) => {
            sel?.insertNodes([
              $createAttachmentNode({
                id: u.id,
                name: u.name,
                size: u.size,
                mime: u.mime,
                url: u.url,
                kbLabel: 'KB',
              }),
            ]);
            if (idx < uploaded.length - 1) {
              sel?.insertText(' ');
            }
          });
        }
      });
    },
    [editor, onUploadFiles],
  );
}

function KeyHandler({
  onEnter,
}: {
  onEnter: React.MutableRefObject<(() => void) | null>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const el = editor.getRootElement();
    if (!el) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onEnter.current?.();
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [editor, onEnter]);
  return null;
}

function AttachmentBridgePlugin({
  attachFilesRef,
  onUploadFiles,
}: {
  attachFilesRef: React.MutableRefObject<((files: File[]) => void) | null>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
}) {
  const attachFiles = useAttachmentPasting(onUploadFiles);
  useEffect(() => {
    attachFilesRef.current = (files: File[]) => void attachFiles(files);
    return () => {
      attachFilesRef.current = null;
    };
  }, [attachFiles, attachFilesRef]);
  return null;
}

function DefaultBottomBar({
  onSend,
  insertMentionTrigger,
  disableSend,
  isLoading,
}: {
  onSend: () => Promise<void>;
  insertMentionTrigger: () => void;
  disableSend: boolean;
  isLoading: boolean;
}) {
  return (
    <div className={css.editorBottomBar}>
      <div style={{display: 'flex', gap: '0.25rem'}}>
        <Button
          type="ghost"
          size="small"
          iconLeftName="at"
          classNames={{icon: css.atIcon}}
          onClick={insertMentionTrigger}
        />
      </div>
      <div style={{display: 'flex', gap: '0.25rem'}}>
        <Button
          size="small"
          disabled={disableSend}
          isLoading={isLoading}
          type="gradient"
          iconLeftName="send"
          iconLeftType="solid"
          onClick={() => void onSend()}
        ></Button>
      </div>
    </div>
  );
}

/* ================= Main Editor Component ================ */
export const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      placeholder: _placeholder,
      fetchMentionSuggestions,
      onUploadFiles,
      onSend,
      className,
      renderBottomBar,
      disableSend = false,
      autoFocus = false,
    },
    ref,
  ) => {
    const {t} = useLabelTranslation();
    const initialConfig = useMemo(
      () => ({
        namespace: 'chat-composer',
        onError: (e: unknown) => {
          // eslint-disable-next-line no-console
          console.error(e);
        },
        theme: EditorTheme(),
        nodes: [MentionNode, AttachmentNode],
      }),
      [],
    );
    const attachFilesRef = useRef<((files: File[]) => void) | null>(null);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({
      multiple: true,
      noClick: true,
      onDrop: (files) => void attachFilesRef.current?.(files),
    });
    const onEnter = useRef<(() => void) | null>(null);

    const [editor, setEditor] = useState<any>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [isSending, setIsSending] = useState(false);

    // Helper function to check if editor content is empty
    const checkIfEditorEmpty = useCallback(() => {
      if (!editor) {
        return true;
      }
      let isEmpty = true;
      editor.getEditorState().read(() => {
        const root = $getRoot();
        const md = $convertToMarkdownString(TRANSFORMERS);
        const nodes = root.getChildren();
        let hasAttachments = false;
        nodes.forEach((n: any) => {
          if (n instanceof AttachmentNode) {
            hasAttachments = true;
          }
        });
        isEmpty = !md.trim() && !hasAttachments;
      });
      return isEmpty;
    }, [editor]);

    const insertMentionTrigger = useCallback(() => {
      if (!editor) {
        return;
      }
      editor.update(() => {
        const sel = $getSelection();
        if (!$isRangeSelection(sel)) {
          return;
        }
        try {
          const anchorNode = sel.anchor.getNode();
          let needsSpace = true;
          if (anchorNode instanceof TextNode) {
            const text = anchorNode.getTextContent();
            const idx = sel.anchor.offset - 1;
            if (idx < 0) {
              needsSpace = false; // at start of node
            } else {
              const ch = text[idx];
              needsSpace = ch !== ' ' && ch !== '\n' && ch !== '\t';
            }
          }
          sel.insertText(needsSpace ? ' @' : '@');
        } finally {
          // ensure focus so typeahead can appear
          editor.focus();
        }
      });
    }, [editor]);

    const extractAndSend = useCallback(async () => {
      if (!editor) {
        return;
      }
      let md = '';
      const attachments: AttachmentAttrs[] = [];
      editor.getEditorState().read(() => {
        md = $convertToMarkdownString(TRANSFORMERS);
        const root = $getRoot();
        const nodes = root.getChildren();
        nodes.forEach((n: any) => {
          if (n instanceof AttachmentNode) {
            attachments.push((n as AttachmentNode).__attrs);
          }
        });
      });
      const parts: Part[] = [];
      if (md.trim()) {
        parts.push({type: 'plain_text', data: md.trim()});
      }
      if (attachments.length) {
        // parts.push({type: 'widget', data: {attachments}});
      }
      if (parts.length) {
        setIsSending(true);
        try {
          await onSend(parts);
          editor.update(() => {
            $getRoot().clear();
          });
        } catch (error) {
          logger.error('Error sending editor content', error);
        } finally {
          setIsSending(false);
        }
      }
    }, [editor, onSend]);

    // Setup onEnter callback
    useEffect(() => {
      onEnter.current = () => void extractAndSend();
      return () => {
        onEnter.current = null;
      };
    }, [extractAndSend]);

    // Track editor content changes
    useEffect(() => {
      if (!editor) {
        return;
      }
      setIsEditorEmpty(checkIfEditorEmpty());

      const removeListener = editor.registerUpdateListener(() => {
        setIsEditorEmpty(checkIfEditorEmpty());
      });

      return () => {
        removeListener();
      };
    }, [editor, checkIfEditorEmpty]);

    // Plugin to capture editor instance
    function EditorRefPlugin() {
      const [editorInstance] = useLexicalComposerContext();
      useEffect(() => {
        setEditor(editorInstance);
      }, [editorInstance]);
      return null;
    }

    useImperativeHandle(
      ref,
      () => ({
        setPlainText: (text: string) => {
          if (!editor) {
            return;
          }
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(text));
            root.append(paragraph);
          });
          editor.focus();
        },
        focus: () => editor?.focus?.(),
        clear: () => {
          if (!editor) {
            return;
          }
          editor.update(() => {
            $getRoot().clear();
          });
        },
      }),
      [editor],
    );

    return (
      <div
        {...getRootProps()}
        className={`${css.root} ${isDragActive ? css.dragActive : ''} ${
          className || ''
        }`}
        tabIndex={-1}
        onClick={() => editor?.focus()}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <div className={css.editorContainer}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={css.contentEditable}
                  aria-placeholder={t('Ask_me_anything', 'Ask me anything')}
                  placeholder={
                    <div className={css.editorPlaceholder}>
                      {t('Ask_me_anything', 'Ask me anything')}
                    </div>
                  }
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            {autoFocus && <AutoFocusPlugin />}
            <MentionTypeahead
              fetchMentionSuggestions={fetchMentionSuggestions}
            />
            <KeyHandler onEnter={onEnter} />
            <AttachmentBridgePlugin
              attachFilesRef={attachFilesRef}
              onUploadFiles={onUploadFiles}
            />
            <EditorRefPlugin />
            {renderBottomBar ? (
              renderBottomBar({onSend: extractAndSend, insertMentionTrigger})
            ) : (
              <DefaultBottomBar
                onSend={extractAndSend}
                insertMentionTrigger={insertMentionTrigger}
                disableSend={isEditorEmpty || disableSend}
                isLoading={isSending}
              />
            )}
          </div>
        </LexicalComposer>
        <input {...getInputProps()} hidden />
      </div>
    );
  },
);
